import React, { useEffect, useState } from 'react';
import Navbar from './components/Navbar';
import Hero, { scrollToTemplates } from './components/Hero';
import GlobalWebsiteSearch from './components/GlobalWebsiteSearch';
import WebsitesListPage from './components/WebsitesListPage';
import CustomWebsiteModal from './components/CustomWebsiteModal';
import SearchSuggestionModal from './components/SearchSuggestionModal';
import Plans from './components/Plans';
import Disclaimer from './components/Disclaimer';
import SellOnline from './components/SellOnline';
import LegalPage from './legal/LegalPage';
import { termsFor } from './legal/terms';
import { privacyFor } from './legal/privacy';
import { refundFor } from './legal/refund';
import { deliveryFor } from './legal/delivery';
import { useOwnezzyContact, whatsappLink } from './legal/contact';
import Footer from './components/Footer';
import TemplatePreview from './components/TemplatePreview';
import StoreDesigns from './components/StoreDesigns';
import AboutPage from './about/AboutPage';
import ContactPage from './about/ContactPage';
import GuidePage from './guide/GuidePage';
import PartnerPage from './partner/PartnerPage';
import BlogPage from './blog/BlogPage';
import NicheLandingPage from './niches/NicheLandingPage';
import SeoArticle from './components/SeoArticle';
import { HOME_ARTICLE, PLANS_ARTICLE } from './seo/seoContent';
import AnnouncementBar from './components/AnnouncementBar';
import AiDogChatBot from './components/AiDogChatBot';
import { trackVisit } from './utils/track';
import { loadBrandingData } from './components/OwnezzyLogo';

function App() {
  const path = window.location.pathname.replace(/\/+$/, '');
  const contact = useOwnezzyContact();
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [customModalCategory, setCustomModalCategory] = useState('');
  const [customModalTemplate, setCustomModalTemplate] = useState('');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchModalQuery, setSearchModalQuery] = useState('');

  // Global event listener for custom website modal
  useEffect(() => {
    const handler = (e) => {
      setCustomModalCategory(e.detail?.category || '');
      setCustomModalTemplate(e.detail?.template || '');
      setIsCustomModalOpen(true);
    };
    window.addEventListener('oz:open-custom-website-modal', handler);
    return () => window.removeEventListener('oz:open-custom-website-modal', handler);
  }, []);

  // Global event listener for search modal & Cmd+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleSearchOpen = (e) => {
      setSearchModalQuery(e.detail?.query || '');
      setIsSearchModalOpen(true);
    };
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setIsSearchModalOpen((prev) => !prev);
      }
    };
    window.addEventListener('oz:open-search-modal', handleSearchOpen);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('oz:open-search-modal', handleSearchOpen);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // One visit per browser session for the Super Admin's Analytics tab
  useEffect(() => { trackVisit(); }, []);

  // Dynamic Homepage SEO (Title & Meta Description configured by Super Admin)
  useEffect(() => {
    if (path !== '') return;
    loadBrandingData().then((data) => {
      if (!data?.seo) return;
      if (data.seo.title) {
        document.title = data.seo.title;
        document.querySelector('meta[property="og:title"]')?.setAttribute('content', data.seo.title);
        document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', data.seo.title);
      }
      if (data.seo.description) {
        document.querySelector('meta[name="description"]')?.setAttribute('content', data.seo.description);
        document.querySelector('meta[property="og:description"]')?.setAttribute('content', data.seo.description);
        document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', data.seo.description);
      }
      if (data.seo.keywords) {
        document.querySelector('meta[name="keywords"]')?.setAttribute('content', data.seo.keywords);
      }
      if (data.seo.favicon) {
        let iconLink = document.querySelector("link[rel*='icon']");
        if (!iconLink) {
          iconLink = document.createElement('link');
          iconLink.rel = 'icon';
          document.head.appendChild(iconLink);
        }
        iconLink.href = data.seo.favicon;
      }
    });
  }, [path]);

  // Links from other pages (/about, /terms) open the home page at a section, e.g. /#plans
  useEffect(() => {
    if (path !== '' || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    const timer = setTimeout(() => {
      if (id === 'select-website') scrollToTemplates();
      else document.getElementById(id)?.scrollIntoView({ block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, [path]);

  const renderPage = () => {
    if (path === '/websites' || path === '/templates') return <WebsitesListPage />;
    if (path.startsWith('/create/')) {
      const slug = decodeURIComponent(path.slice('/create/'.length));
      return <NicheLandingPage slug={slug} />;
    }
    if (path === '/create') return <NicheLandingPage slug="clothing-website" />;
    if (path === '/about') return <AboutPage />;
    if (path === '/contact') return <ContactPage />;
    // The User Guide: /guide (every guide) and /guide/<slug> (one guide; #step links to a step)
    if (path === '/guide') return <GuidePage />;
    if (path.startsWith('/guide/')) return <GuidePage slug={decodeURIComponent(path.slice('/guide/'.length))} />;
    if (path === '/partner') return <PartnerPage />;
    if (path === '/blog') return <BlogPage />;
    if (path.startsWith('/blog/')) return <BlogPage slug={decodeURIComponent(path.slice('/blog/'.length))} />;
    if (path === '/terms') return <LegalPage docFor={termsFor} />;
    if (path === '/privacy') return <LegalPage docFor={privacyFor} />;
    if (path === '/refund-policy') return <LegalPage docFor={refundFor} />;
    if (path === '/delivery-policy' || path === '/shipping-policy') return <LegalPage docFor={deliveryFor} />;

    if (window.location.pathname === '/preview' || new URLSearchParams(window.location.search).has('preview')) {
      return <TemplatePreview />;
    }

    return (
      <div className="app-shell">
        <AnnouncementBar />
        <Navbar />
        <main>
          <Hero onCreate={scrollToTemplates} />
          {/* Global Search with Top 10 Suggestions & Highlighted Custom Website Button */}
          <GlobalWebsiteSearch
            onOpenCustomModal={(category) => {
              setCustomModalCategory(category || '');
              setIsCustomModalOpen(true);
            }}
          />
          <StoreDesigns />
          <Plans />
          <SellOnline />
          <SeoArticle article={HOME_ARTICLE} />
          <SeoArticle article={PLANS_ARTICLE} />
          <Disclaimer />
        </main>
        <Footer />

        {/* Floating WhatsApp Help Button */}
        <a
          href={whatsappLink(contact, 'Hi, I need help setting up my Ownezzy store.')}
          target="_blank"
          rel="noreferrer"
          className="whatsapp-float"
          aria-label="Need help? Chat with us on WhatsApp"
        >
          <i className="bi bi-whatsapp" />
          <span>Need help?</span>
        </a>

      </div>
    );
  };

  return (
    <>
      {renderPage()}
      {/* Floating Animated Dog Mascot & AI Chat Assistant on Top Right */}
      <AiDogChatBot />

      {/* Global Search & Suggestion Modal */}
      <SearchSuggestionModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        initialQuery={searchModalQuery}
      />

      {/* Global Custom Website Creation Modal */}
      <CustomWebsiteModal
        isOpen={isCustomModalOpen}
        onClose={() => setIsCustomModalOpen(false)}
        initialCategory={customModalCategory}
        initialTemplate={customModalTemplate}
      />
    </>
  );
}

export default App;
